// Sign-in for the chaos demo: the staff realm only (wxchange-auth
// docs/realms.md). Adapted from wxchange-demo-viewer src/lib/auth.js with the
// customers realm, realm switching and passcode mode removed.
//
//   keycloak – keycloak-js, login-required, PKCE S256; every API call carries
//              the user's bearer. What the clusters run.
//   none     – no sign-in (local development, where the API runs with
//              AUTH_DISABLED=true).

/**
 * @param {{authMode: string, keycloakUrl: string, realm: string, clientId: string}} cfg
 * @param {{loadKeycloak?: () => Promise<any>, fetchImpl?: typeof fetch}} [deps]
 */
export function createAuth(cfg, { loadKeycloak, fetchImpl } = {}) {
  const doFetch = fetchImpl || ((...a) => fetch(...a));
  let kc = null;

  if (cfg.authMode !== "keycloak") {
    return {
      mode: "none",
      init: async () => true,
      fetch: doFetch,
      user: () => ({ name: "Local developer", email: "", staff: true }),
      logout: async () => {},
    };
  }

  async function init() {
    const Keycloak = await (loadKeycloak ? loadKeycloak() : import("keycloak-js").then((m) => m.default));
    kc = new Keycloak({ url: cfg.keycloakUrl, realm: cfg.realm, clientId: cfg.clientId });
    return kc.init({ onLoad: "login-required", pkceMethod: "S256", checkLoginIframe: false });
  }

  async function authFetch(url, init) {
    if (kc) {
      try { await kc.updateToken(30); } catch { /* session dead: let the 401 surface */ }
    }
    const headers = new Headers((init && init.headers) || {});
    if (kc && kc.token) headers.set("Authorization", `Bearer ${kc.token}`);
    return doFetch(url, { ...(init || {}), headers });
  }

  function user() {
    const c = kc && kc.tokenParsed;
    if (!c) return null;
    const iss = typeof c.iss === "string" ? c.iss : "";
    return {
      name: c.name || c.preferred_username || c.email || "",
      email: c.email || "",
      // Staff is decided by the token's issuer, never by a role.
      staff: iss.endsWith(`/realms/${cfg.realm}`),
    };
  }

  async function logout() {
    if (kc) await kc.logout({ redirectUri: window.location.origin + window.location.pathname });
  }

  return { mode: "keycloak", init, fetch: authFetch, user, logout };
}
